import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import { auth } from "../../config/firebaseConfig";
import { signOut } from "firebase/auth";
import circleQuestion from "../../assets/icons/circle-question.svg";
import gear from "../../assets/icons/gear.svg";
import headset from "../../assets/icons/headset.svg";
import rightFromBracket from "../../assets/icons/right-from-bracket.svg";
import user from "../../assets/icons/user.svg";


export const ProfilePopup = ({ openOverlayModal }) => {
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const handleLogout = async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        
        console.log("🔴 [LOGOUT] Starting logout process...");
        
        try {
            // Sign out from Firebase Auth
            await signOut(auth);
            console.log("✅ [LOGOUT] Firebase sign out successful");
            
            // Clear all authentication-related local storage items
            const itemsToRemove = [
                "authToken",
                "userRole",
                "profilePhoto",
                "displayName"
            ];
            
            itemsToRemove.forEach(item => {
                localStorage.removeItem(item);
                console.log(`🗑️ [LOGOUT] Removed: ${item}`);
            });
            
            // Also clear any user-specific training consent items
            const allKeys = Object.keys(localStorage);
            allKeys.forEach(key => {
                if (key.startsWith("training_consent_")) {
                    localStorage.removeItem(key);
                    console.log(`🗑️ [LOGOUT] Removed: ${key}`);
                }
            });
            
            console.log("✅ [LOGOUT] All user data cleared");
            console.log("🚀 [LOGOUT] Redirecting to auth page...");
            
            // Redirect to auth page
            navigate("/auth", { replace: true });
        } catch (error) {
            console.error("❌ [LOGOUT] Error:", error);
            
            // Even if there's an error, clear local data and redirect
            localStorage.removeItem("authToken");
            localStorage.removeItem("userRole");
            localStorage.removeItem("profilePhoto");
            localStorage.removeItem("displayName");
            
            console.log("⚠️ [LOGOUT] Forced cleanup and redirect due to error");
            navigate("/auth", { replace: true });
        } finally {
            setIsLoggingOut(false);
        }
    };

    const menuItems = [
        { label: "Profile", icon: user, onClick: () => openOverlayModal("settings", { tab: "account" }) },
        { label: "Setting", icon: gear, onClick: () => openOverlayModal("settings") },
        { label: "Help & Support", icon: headset, onClick: () => openOverlayModal("settings", { tab: "support" }) },
        { label: "FAQs", icon: circleQuestion, onClick: () => openOverlayModal("settings", { tab: "faq" }) },
        { 
            label: isLoggingOut ? "Logging out..." : "Log out", 
            icon: rightFromBracket, 
            color: "#c74f4f", 
            onClick: handleLogout,
        },
    ];

    return (
        <Box
            sx={{
                backgroundColor: "#e1ecc8",
                borderRadius: "0 0 20px 20px",
                width: "100%",
                padding: { xs: "15px", sm: "20px" },
                paddingTop: "30px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                alignItems: "stretch",
                boxShadow: '0 4px 16px rgba(81, 109, 79, 0.4), 0 2px 8px rgba(81, 109, 79, 0.3)',
            }}
        >
            {menuItems.map((item, index) => (
                <Box
                    key={index}
                    onClick={!isLoggingOut ? item.onClick : undefined}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: "12px",
                        width: "100%",
                        padding: "12px",
                        borderRadius: "12px",
                        cursor: isLoggingOut ? "not-allowed" : "pointer",
                        opacity: isLoggingOut && item.label === "Logging out..." ? 0.6 : 1,
                        transition: "background 0.3s",
                        "&:hover": {
                            backgroundColor: !isLoggingOut ? "#d1dfb5" : "transparent",
                        },
                    }}
                >
                    {isLoggingOut && item.label === "Logging out..." ? (
                        <CircularProgress size={22} sx={{ color: "#c74f4f" }} />
                    ) : (
                        <img src={item.icon} alt={item.label} style={{ width: "24px", height: "24px" }} />
                    )}
                    <Typography
                        sx={{
                            fontFamily: "Poppins, sans-serif",
                            fontSize: { xs: "16px", sm: "18px" }, // Responsive font size
                            fontWeight: 600,
                            color: item.color || "#516d4f",
                        }}
                    >
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
